import clsx from "clsx";
import * as React from "react";
import { ReactNode, useState } from "react";
import { AriaButtonProps, AriaNumberFieldProps, useButton, useLocale, useNumberField } from "react-aria";
import { useNumberFieldState } from "react-stately";
import { useOnClickOutside } from "usehooks-ts";
import { Theme } from "react-migration/lib/theme/Theme";

export type NumericInputSize = "small" | "medium" | "large";
enum DisabledTextContentColor {
  Light = "text-text-base-primary-disabled",
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  Dark = "text-text-base-primary-disabled",
}

export type NumericInputProps = {
  size?: NumericInputSize;
  length?: number;
  className?: string;
  units?: string | ReactNode;
  unitsPrefix?: boolean;
  alwaysShowUnits?: boolean;
  theme?: Theme;
  hideSteppers?: boolean;
  errorMessage?: string;
} & AriaNumberFieldProps;

type StepperButtonProps = {
  className?: string;
} & AriaButtonProps;

const borderInput = "border-border-action";
const borderInputFocus = "border-border-action-focus";

const StepperButton: React.FC<StepperButtonProps> = (props) => {
  const ref = React.useRef<HTMLButtonElement>(null);
  const { buttonProps } = useButton(props, ref);
  return (
    <button {...buttonProps} ref={ref} className={clsx("flex items-center border-none h-1/2", props.className)}>
      {props.children}
    </button>
  );
};

export const NumericInput: React.FC<NumericInputProps> = (props) => {
  const theme = props.theme || Theme.Light;
  const { locale } = useLocale();

  const inputRef = React.useRef<HTMLInputElement>(null);
  const [rawInputValue, setRawInputValue] = useState<string>("");

  const state = useNumberFieldState({
    ...props,
    locale,
  });

  const {
    labelProps,
    groupProps,
    inputProps: ariaInputProps,
    incrementButtonProps,
    decrementButtonProps,
  } = useNumberField({ ...props, "aria-label": String(props.label) }, state, inputRef);

  // HACK: When we provide a formatter to useNumberField, it can override user input
  // at odd times. Documentation say its on blur, but its almost more like a debounce.
  // This may be coming from our own internal state, but I'm not sure. This ensures the
  // user never wrestles with the input, but a better solution should exist that doesn't
  // require fighting with the library we're using
  const inputProps = {
    ...ariaInputProps,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setRawInputValue(newValue);

      if (ariaInputProps.onChange) {
        ariaInputProps.onChange(e);
      }
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      if (rawInputValue.endsWith(".")) {
        setRawInputValue(rawInputValue.slice(0, -1));
      }

      if (ariaInputProps.onBlur) {
        ariaInputProps.onBlur(e);
      }
    },
    get value() {
      if (rawInputValue.endsWith(".")) {
        return rawInputValue;
      }
      return ariaInputProps.value;
    },
  };

  const size = props.size || "medium";

  const divRef = React.useRef<HTMLDivElement>(null);
  const [borderColor, setBorderColor] = useState(borderInput);
  useOnClickOutside(divRef, () => setBorderColor(borderInput));

  const inputBaseClasses =
    "w-full peer font-sans text-sm leading-none p-1 outline-hidden placeholder:text-text-base-tertiary";

  const sizeClasses = {
    small: "py-1",
    medium: "py-2",
    large: "py-[10px]",
  };
  const inputSizeClass = sizeClasses[size] || "";
  const inputDisabledClass = props.isDisabled
    ? `text-text-base-primary-disabled-${theme === Theme.Light ? "light" : "dark"}`
    : "";
  const inputTextClass = theme === Theme.Light ? "text-black" : "text-white";

  const divBaseClasses =
    "rounded-sm cursor-text flex relative peer-focus:shadow-inputFocus border border-solid focus:outline-hidden";
  const divDisabledClass = props.isDisabled
    ? `border-border-button-disabled-${theme === Theme.Light ? "light" : "dark"}`
    : borderColor;
  const divErrorClass = props.errorMessage ? "border border-red-700" : "";
  const divBackgroundClass = theme === Theme.Light ? "bg-white" : "bg-transparent";

  return (
    <div className={clsx("flex flex-col", props.className)}>
      <label
        className={`w-full ${
          props.isDisabled ? `text-text-base-primary-disabled-${theme === Theme.Light ? "light" : "dark"}` : ""
        }`}
        {...labelProps}
      >
        {props.label && <div className="mb-2 text-xs font-semibold leading-5">{props.label}</div>}

        <div
          ref={divRef}
          {...groupProps}
          className={clsx(divBaseClasses, divDisabledClass, divErrorClass, divBackgroundClass)}
        >
          {props.units && props.unitsPrefix && (
            <div
              onClick={() => inputRef.current?.focus()}
              className={`inset-0 min-w-fit justify-end overflow-hidden flex items-center text-sm leading-none peer-disabled:${DisabledTextContentColor[theme]}`}
            >
              <span className="pl-3 grow shrink-0 text-text-base-tertiary">{`${props.units} `}</span>
            </div>
          )}
          <input
            {...inputProps}
            ref={inputRef}
            size={props.length}
            min={0}
            onFocus={() => setBorderColor(borderInputFocus)}
            className={clsx(inputBaseClasses, inputSizeClass, inputDisabledClass, inputTextClass)}
          />
          {props.units && !props.unitsPrefix && (
            <div
              onClick={() => inputRef.current?.focus()}
              className={`inset-0 min-w-fit justify-end pr-1.5 overflow-hidden flex items-center text-sm leading-none peer-disabled:${DisabledTextContentColor[theme]}`}
            >
              <span
                className={clsx("shrink-0 grow text-right text-text-base-tertiary", {
                  ["invisible"]: !state.inputValue && !props.alwaysShowUnits,
                })}
              >
                {` ${props.units}`}
              </span>
            </div>
          )}
          {!props.hideSteppers && (
            <div
              className={clsx(
                "flex min-w-[30px] flex-col -ml-px border-l border-0 border-solid border-l-border-action h-[30px]",
                {
                  ["h-[30px]"]: size === "small",
                  ["h-[34px]"]: size === "medium",
                  ["h-[38px]"]: size === "large",
                }
              )}
            >
              <StepperButton
                {...incrementButtonProps}
                isDisabled={props.isDisabled || inputProps.value === props.maxValue ? true : false}
                className={clsx("justify-center border-t-0 rounded-br cursor-not-allowed bg-blue-grey-100", {
                  ["hover:bg-blue-grey-200 cursor-pointer"]: !incrementButtonProps.isDisabled,
                })}
              >
                <i className="text-xs leading-none icon-lt-arrow-up-s-line" />
              </StepperButton>
              <StepperButton
                {...decrementButtonProps}
                isDisabled={props.isDisabled || inputProps.value === props.minValue ? true : false}
                className={clsx("justify-center border-t-0 rounded-br cursor-not-allowed bg-blue-grey-100", {
                  ["hover:bg-blue-grey-200 cursor-pointer"]: !decrementButtonProps.isDisabled,
                })}
              >
                <i className="text-xs leading-none icon-lt-arrow-down-s-line" />
              </StepperButton>
            </div>
          )}
        </div>
      </label>
      {props.errorMessage && (
        <p className="pt-1 m-0 text-xs font-normal leading-5 text-red-700">{props.errorMessage}</p>
      )}
    </div>
  );
};

export default NumericInput;
